/**
 * Delete Context Menu
 *
 * Right-click context menu for delete operations on infrastructure endpoints.
 * Similar to MapButtonContextMenu but focused on delete actions.
 */

import { useRef, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DeleteSelectedButton,
  DeleteSingleButton,
  MenuSeparator,
} from './delete-context-menu/delete-context-menu.parts'
import {
  useDismissOnOutsideOrEscape,
  useViewportClampedPosition,
} from './delete-context-menu/use-delete-menu-position'

interface DeleteContextMenuProps {
  x: number
  y: number
  hasSelection: boolean
  selectionCount: number
  onClose: () => void
  onDeleteSingle: () => void
  onDeleteSelected: () => void
}

export function DeleteContextMenu({
  x,
  y,
  hasSelection,
  selectionCount,
  onClose,
  onDeleteSingle,
  onDeleteSelected,
}: DeleteContextMenuProps): ReactElement {
  const { t } = useTranslation('cases')
  const menuRef = useRef<HTMLDivElement>(null)
  useDismissOnOutsideOrEscape(menuRef, onClose)
  const position = useViewportClampedPosition(menuRef, x, y)
  const wrap = (fn: () => void) => (): void => {
    fn()
    onClose()
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
      style={{ left: position.x, top: position.y }}
    >
      <DeleteSingleButton onActivate={wrap(onDeleteSingle)} t={t} />
      <MenuSeparator />
      <DeleteSelectedButton
        onActivate={wrap(onDeleteSelected)}
        hasSelection={hasSelection}
        selectionCount={selectionCount}
        t={t}
      />
    </div>
  )
}
